import { useEffect, useRef, useState } from 'react'
import './ComboBox.css'
import { Locale, useLang } from './Locale'

type Option = {
  en: string
  zh_cn: string
  zh_hk: string
  preview_url?: string
}

interface ComboBoxProps {
  className?: string
  buttonClassName?: string
  data: Option[]
  styleCounter?: Record<string, number>
  value: string
  onChange?: (selectedOption: Option) => void
}

export default function ComboBox({
  className = '',
  buttonClassName,
  data,
  styleCounter,
  value,
  onChange,
}: ComboBoxProps) {
  // useHook
  const { lang } = useLang()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [optionIndex, setOptionIndex] = useState<number>(0)
  const [searchTerm, setSearchTerm] = useState<string>('')

  // useRef
  const comboboxRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // function
  const handleToggleDropdown = () => setIsOpen(prev => !prev)
  const handleSelectOption = ({ en, index }: { en: string; index: number }) => {
    let targetIndex = index
    if (en !== '(all)') {
      targetIndex = data.findIndex(option => option.en === en)
    }
    setOptionIndex(targetIndex)
    setIsOpen(false)
    setSearchTerm('')

    if (onChange && data[targetIndex]) {
      onChange(data[targetIndex])
    }
  }
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsOpen(true)
    setSearchTerm(event.currentTarget.value)
  }

  // useEffect
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        comboboxRef.current &&
        !comboboxRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className={`combobox ${className}`} ref={comboboxRef}>
      <div className="combobox-container">
        <input
          className="combobox-input"
          ref={inputRef}
          value={searchTerm}
          placeholder={searchTerm === '' ? data[optionIndex][lang] : searchTerm}
          onChange={handleInputChange}
        />
        <button
          type="button"
          onClick={handleToggleDropdown}
          className={buttonClassName}
        >
          <i
            className={
              !isOpen ? 'fa-solid fa-caret-down' : 'fa-solid fa-caret-up'
            }
          />
        </button>
      </div>
      {isOpen && (
        <div className="combobox-dropdown">
          {data.length > 0 &&
            data
              .filter(option =>
                searchTerm.length > 0
                  ? Object.values(option).some(text =>
                      text.toLowerCase().includes(searchTerm),
                    )
                  : true,
              )
              .map(({ en, zh_cn, zh_hk, preview_url }, index) => (
                <li
                  className="combobox-dropdown-item"
                  key={index}
                  onClick={() => handleSelectOption({ en, index })}
                >
                  {styleCounter && (
                    <span className="styleCounter">{`(${
                      styleCounter[en] ? styleCounter[en] : '0'
                    })`}</span>
                  )}
                  {preview_url && <img className="image" src={preview_url} />}
                  <Locale en={en} zh_cn={zh_cn} zh_hk={zh_hk} />
                </li>
              ))}
        </div>
      )}
    </div>
  )
}
